import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useSidebar } from './AppContext';

export interface WalkthroughStep {
  path: string;
  label: string;
  description: string;
  /** DOM id from sidebar item nativeID, e.g. walkthrough-sidebar-dashboard */
  targetId: string;
}

const DASHBOARD_STEPS: WalkthroughStep[] = [
  {
    path: '/dashboard',
    label: 'walkthrough.overview_label',
    description: 'walkthrough.overview_description',
    targetId: 'walkthrough-sidebar-dashboard',
  },
  {
    path: '/dashboard/universe',
    label: 'walkthrough.universe_label',
    description: 'walkthrough.universe_description',
    targetId: 'walkthrough-sidebar-dashboard-universe',
  },
  {
    path: '/dashboard/aggregations',
    label: 'walkthrough.aggregations_label',
    description: 'walkthrough.aggregations_description',
    targetId: 'walkthrough-sidebar-dashboard-aggregations',
  },
  {
    path: '/dashboard/performance',
    label: 'walkthrough.performance_label',
    description: 'walkthrough.performance_description',
    targetId: 'walkthrough-sidebar-dashboard-performance',
  },
];

const ADMIN_STEPS: WalkthroughStep[] = [
  {
    path: '/dashboard/admin/teams',
    label: 'walkthrough.teams_label',
    description: 'walkthrough.teams_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-teams',
  },
  {
    path: '/dashboard/admin/users',
    label: 'walkthrough.users_label',
    description: 'walkthrough.users_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-users',
  },
  {
    path: '/dashboard/admin/notifications',
    label: 'walkthrough.notifications_label',
    description: 'walkthrough.notifications_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-notifications',
  },
  {
    path: '/dashboard/admin/data',
    label: 'walkthrough.admin_data_label',
    description: 'walkthrough.admin_data_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-data',
  },
  {
    path: '/dashboard/admin/leads',
    label: 'walkthrough.leads_label',
    description: 'walkthrough.leads_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-leads',
  },
  {
    path: '/dashboard/admin/plans',
    label: 'walkthrough.plans_label',
    description: 'walkthrough.plans_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-plans',
  },
  {
    path: '/dashboard/admin/ai',
    label: 'walkthrough.ai_label',
    description: 'walkthrough.ai_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-ai',
  },
  {
    path: '/dashboard/admin/reviews',
    label: 'walkthrough.reviews_label',
    description: 'walkthrough.reviews_description',
    targetId: 'walkthrough-sidebar-dashboard-admin-reviews',
  },
];

interface WalkthroughContextType {
  isOpen: boolean;
  stepIndex: number;
  steps: WalkthroughStep[];
  start: () => void;
  next: () => void;
  prev: () => void;
  close: () => void;
}

const WalkthroughContext = createContext<WalkthroughContextType | undefined>(undefined);

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (ctx === undefined) throw new Error('useWalkthrough must be used within WalkthroughProvider');
  return ctx;
}

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const isAdmin = user?.role === 'admin' || user?.role === 'ADMIN';
  const steps = isAdmin ? ADMIN_STEPS : DASHBOARD_STEPS;

  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const start = useCallback(() => {
    if (isCollapsed) toggleSidebar();
    setStepIndex(0);
    setIsOpen(true);
  }, [isCollapsed, toggleSidebar]);

  const next = useCallback(() => {
    setStepIndex((i) => (i >= steps.length - 1 ? i : i + 1));
  }, [steps.length]);

  const prev = useCallback(() => {
    setStepIndex((i) => (i <= 0 ? 0 : i - 1));
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <WalkthroughContext.Provider
      value={{
        isOpen,
        stepIndex,
        steps,
        start,
        next,
        prev,
        close,
      }}
    >
      {children}
    </WalkthroughContext.Provider>
  );
}
